import { useEditorRuntimeStore } from '../stores/useEditorRuntimeStore';
import { type BoundsWithCenter } from '../utils/editor/bounds';
import { viewportCoordsToSceneCoords } from '../utils/editor/coords';

import { type GenericElement } from './element';
import { getPlugin, type BoundsOptions } from './plugin';

export const boundsCache = new Map<
  string,
  { bounds: BoundsWithCenter; version: string }
>([]);

export const getElementBounds = (
  element: GenericElement,
  options: BoundsOptions = { withRotation: true },
) => {
  const cacheKey = `${element.id}-${options.withRotation ? 'r' : 'n'}`;

  const cachedBounds = boundsCache.get(cacheKey);

  if (cachedBounds && cachedBounds.version === element.version.bounds) {
    return cachedBounds.bounds;
  }

  const plugin = getPlugin(element.type);
  const bounds = plugin.collision.bounds(element, options);

  boundsCache.set(cacheKey, { bounds, version: element.version.bounds });
  return bounds;
};

export const getCommonBounds = (
  elements: GenericElement[],
): BoundsWithCenter => {
  if (!elements.length) return [0, 0, 0, 0, 0, 0];

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  elements.forEach((element) => {
    const [x1, y1, x2, y2] = getElementBounds(element);
    minX = Math.min(minX, x1);
    minY = Math.min(minY, y1);
    maxX = Math.max(maxX, x2);
    maxY = Math.max(maxY, y2);
  });

  return [minX, minY, maxX, maxY, (minX + maxX) / 2, (minY + maxY) / 2];
};

export const getGenericBounds = (element: GenericElement): BoundsWithCenter => {
  return [
    element.x,
    element.y,
    element.x + element.width,
    element.y + element.height,
    element.x + element.width / 2,
    element.y + element.height / 2,
  ];
};

export const isElementInViewport = (
  element: GenericElement,
  width: number,
  height: number,
) => {
  const { offsetLeft, offsetTop, scrollX, scrollY, zoom } =
    useEditorRuntimeStore.getState();
  const viewTransformations = {
    offsetLeft,
    offsetTop,
    scrollX,
    scrollY,
    zoom: zoom.value,
  };

  const [x1, y1, x2, y2] = getElementBounds(element);

  const topLeftSceneCoords = viewportCoordsToSceneCoords(
    {
      clientX: viewTransformations.offsetLeft,
      clientY: viewTransformations.offsetTop,
    },
    viewTransformations,
  );

  const bottomRightSceneCoords = viewportCoordsToSceneCoords(
    {
      clientX: viewTransformations.offsetLeft + width,
      clientY: viewTransformations.offsetTop + height,
    },
    viewTransformations,
  );

  return (
    topLeftSceneCoords.x <= x2 &&
    topLeftSceneCoords.y <= y2 &&
    bottomRightSceneCoords.x >= x1 &&
    bottomRightSceneCoords.y >= y1
  );
};
